/**
 * 元码科技文档列表/详情字段映射与请求参数
 * @see https://www.anqicms.com/anqiapi-archive/141.html
 * @see https://www.anqicms.com/anqiapi-archive/142.html
 */

function formatDate(timestamp) {
  if (!timestamp) return ''
  const ms = timestamp > 1e12 ? timestamp : timestamp * 1000
  return new Date(ms).toISOString().split('T')[0]
}

export function extractExtraValue(extra, key) {
  if (!extra || !extra[key]) return ''
  const field = extra[key]
  if (typeof field === 'object' && field !== null) {
    return field.value || field.default || ''
  }
  return String(field)
}

export function resolveImageUrl(url, apiBase) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  const base = (apiBase || '').replace(/\/api\/?$/, '')
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`
}

/**
 * 安企CMS 缩略图路径：/uploads/.../thumb_xxx.png → 原图 /uploads/.../xxx.png
 */
export function toOriginalImageUrl(url) {
  if (!url) return ''
  return String(url).replace(/\/thumb_([^/?#]+)/i, '/$1')
}

/** 正文 HTML 内图片改为原图，并补全相对路径 */
export function processArchiveContent(html, apiBase) {
  if (!html) return ''
  return String(html).replace(
    /(<img\b[^>]*?\s(?:src|data-src)=["'])([^"']+)(["'])/gi,
    (_, prefix, src, suffix) => {
      let url = toOriginalImageUrl(src)
      if (apiBase) url = resolveImageUrl(url, apiBase)
      return `${prefix}${url}${suffix}`
    }
  )
}

export function resolveCategoryName(item, categoryMap = {}) {
  return (
    item.category?.title ||
    categoryMap[item.category_id] ||
    categoryMap[item.categoryId] ||
    '资讯'
  )
}

export function resolveAuthor(item) {
  const fromExtra = extractExtraValue(item.extra, 'author')
  if (fromExtra) return fromExtra
  if (item.user?.user_name) return item.user.user_name
  if (item.user?.nickname) return item.user.nickname
  return '元码科技'
}

/** 详情正文在 data.content，列表可能直接返回 content */
export function resolveArchiveContent(item) {
  if (!item) return ''
  return item.data?.content || item.content || item.description || ''
}

/**
 * 构建文档详情请求参数
 * id 与 filename 二选一；filename=prev/next 可获取上一篇/下一篇
 */
export function buildArchiveDetailParams(options = {}) {
  const { id, filename, password, render = true } = options
  const params = { render }

  if (id !== undefined && id !== null && id !== '') {
    params.id = id
  }
  if (filename) {
    params.filename = filename
  }
  if (password) {
    params.password = password
  }

  return params
}

export function isNumericId(value) {
  return /^\d+$/.test(String(value))
}

/**
 * 构建文档列表请求参数（type=page 分页模式，返回 total）
 */
export function buildArchiveListParams(options = {}) {
  const {
    page = 1,
    limit = 10,
    moduleId,
    categoryId,
    excludeCategoryId,
    order = 'created_time desc',
    q = '',
    flag = '',
    showCategory = true,
    showContent = false,
    showExtra = true,
    child = true,
    type = 'page'
  } = options

  const params = {
    type,
    page,
    limit,
    order,
    showCategory,
    showContent,
    showExtra,
    child
  }

  if (moduleId) params.moduleId = moduleId
  if (categoryId) params.categoryId = String(categoryId)
  if (excludeCategoryId) params.excludeCategoryId = String(excludeCategoryId)
  if (q && String(q).trim()) params.q = String(q).trim()
  if (flag) params.flag = flag

  return params
}

export function mapArchiveToNews(item, categoryMap = {}, apiBase) {
  if (!item) return null

  const cover = item.thumb || item.logo || ''

  return {
    id: item.id,
    title: item.title || '',
    seoTitle: item.seo_title || item.seoTitle || '',
    category: resolveCategoryName(item, categoryMap),
    categoryId: item.category_id || item.categoryId,
    summary: item.description || '',
    content: resolveArchiveContent(item),
    cover: apiBase ? resolveImageUrl(cover, apiBase) : cover,
    author: resolveAuthor(item),
    date: formatDate(item.created_time || item.createdTime),
    views: item.views || 0,
    commentCount: item.comment_count || item.commentCount || 0,
    urlToken: item.url_token || item.urlToken || '',
    link: item.link || item.fixed_link || item.fixedLink || '',
    keywords: item.keywords || '',
    flag: item.flag || '',
    moduleId: item.module_id || item.moduleId
  }
}

/** 详情页完整映射（含正文、SEO）；封面与正文图片均用原图 */
export function mapArchiveDetail(item, apiBase) {
  if (!item) return null

  const categoryMap = item.category?.id
    ? { [item.category.id]: item.category.title }
    : {}

  const news = mapArchiveToNews(item, categoryMap, apiBase)
  if (!news) return null

  const originalCover = item.logo || (Array.isArray(item.images) && item.images[0]) || news.cover
  news.cover = apiBase
    ? resolveImageUrl(toOriginalImageUrl(originalCover), apiBase)
    : toOriginalImageUrl(originalCover)
  news.content = processArchiveContent(news.content, apiBase)

  return news
}

export function mapArchiveList(list, categoryMap = {}, apiBase) {
  if (!Array.isArray(list)) return []
  return list
    .map(item => {
      const mapped = mapArchiveToNews(item, categoryMap)
      if (!mapped) return null
      if (apiBase) {
        mapped.cover = resolveImageUrl(mapped.cover, apiBase)
      }
      return mapped
    })
    .filter(Boolean)
}

export function buildCategoryMap(categories) {
  const map = {}
  if (!Array.isArray(categories)) return map
  categories.forEach(cat => {
    if (cat.id) map[cat.id] = cat.title || cat.name
  })
  return map
}

/** 相关文档列表参数 */
export function buildRelatedArchiveParams(id, options = {}) {
  const { limit = 5, moduleId } = options
  const params = {
    type: 'related',
    id,
    limit,
    showCategory: true
  }
  if (moduleId) params.moduleId = moduleId
  return params
}
